import { cache } from 'react';
import { createClient } from '@/prismicio';
import { logger } from '@/utils/logger';

export interface SiteSettings {
  siteName: string;
  ogSiteName: string;
  alternateNames: string[];
  description: string;
  email: string;
  telephone: string;
  whatsappUrl: string;
  address: {
    streetAddress: string;
    addressLocality: string;
    addressRegion: string;
    postalCode?: string;
    addressCountry: string;
  };
  openingHours: string[];
  socialLinks: string[];
  parentOrganization: {
    name: string;
    url?: string;
  };
}

// Code-side fallbacks — used when the Prismic doc is absent or a field is empty.
// These mirror the values currently hardcoded in layout.tsx so removing the
// hardcoded block does not change behavior until the Prismic doc is populated.
export const SITE_SETTINGS_FALLBACK: SiteSettings = {
  siteName: 'PS Global NetSuite Solutions',
  ogSiteName: 'PS Global',
  alternateNames: ['PS Global', 'PSGlobal Hong Kong', 'PS Global Consulting Hong Kong'],
  description:
    'PS Global (formerly ONE Pacific), part of PS Global Consulting, is your trusted NetSuite Solutions Partner in Asia. We provide implementation, customization, and support services for Oracle NetSuite.',
  email: 'info@onepac.net',
  telephone: '+852 5357 3560',
  whatsappUrl: 'https://wa.me/85253573560',
  address: {
    streetAddress: '135 Bonham Strand Trade Centre, 13/F, Bonham Strand St',
    addressLocality: 'Sheung Wan',
    addressRegion: 'Hong Kong',
    addressCountry: 'Hong Kong',
  },
  openingHours: ['Monday,Tuesday,Wednesday,Thursday,Friday 09:00-18:00'],
  socialLinks: [
    'https://www.linkedin.com/company/one-pacific-consulting-ltd/',
    'https://www.youtube.com/channel/UCdL5vUMIFKfveFIp2cUC1OA',
    'https://wa.me/85253573560',
  ],
  parentOrganization: {
    name: 'PS Global Consulting',
    url: 'https://psglobalconsulting.com',
  },
};

// Prismic link fields hydrate to objects with a `url` property (or are empty).
type PrismicLinkLike = { url?: string | null } | null | undefined;

type SiteSettingsData = {
  site_name?: string | null;
  og_site_name?: string | null;
  alternate_names?: Array<{ value?: string | null }> | null;
  description?: string | null;
  email?: string | null;
  telephone?: string | null;
  whatsapp_url?: string | null;
  street_address?: string | null;
  address_locality?: string | null;
  address_region?: string | null;
  postal_code?: string | null;
  address_country?: string | null;
  opening_hours?: string | null;
  social_links?: Array<{ url?: PrismicLinkLike }> | null;
  parent_organization_name?: string | null;
  parent_organization_url?: PrismicLinkLike;
};

const linkUrl = (link: PrismicLinkLike): string | undefined => {
  const url = link?.url?.trim();
  return url ? url : undefined;
};

const pick = <T extends string | undefined>(value: T | null | undefined, fallback: string): string =>
  value && value.trim() ? value : fallback;

// Build a `tel:` href from a display phone number, e.g. "+852 5357 3560" → "tel:+85253573560".
// Pure + dependency-free so client components (e.g. Footer) can import it too.
export const phoneHref = (telephone: string): string => `tel:${telephone.replace(/[^\d+]/g, '')}`;

export const getSiteSettings = cache(async (lang: string): Promise<SiteSettings> => {
  try {
    const client = createClient();
    // site_settings is a new custom type; until Slice Machine regenerates
    // prismicio-types.d.ts after publishing, cast the id so getSingle accepts it.
    const doc = await (
      client.getSingle as (id: string, options?: { lang: string }) => Promise<{ data: unknown }>
    )('site_settings', { lang });
    const data = doc.data as SiteSettingsData;

    const alternateNames = (data.alternate_names ?? [])
      .map((row) => row?.value?.trim())
      .filter((v): v is string => !!v);

    const socialLinks = (data.social_links ?? [])
      .map((row) => linkUrl(row?.url))
      .filter((v): v is string => !!v);

    const parentUrl = linkUrl(data.parent_organization_url);

    return {
      siteName: pick(data.site_name, SITE_SETTINGS_FALLBACK.siteName),
      ogSiteName: pick(data.og_site_name, SITE_SETTINGS_FALLBACK.ogSiteName),
      alternateNames: alternateNames.length > 0 ? alternateNames : SITE_SETTINGS_FALLBACK.alternateNames,
      description: pick(data.description, SITE_SETTINGS_FALLBACK.description),
      email: pick(data.email, SITE_SETTINGS_FALLBACK.email),
      telephone: pick(data.telephone, SITE_SETTINGS_FALLBACK.telephone),
      whatsappUrl: pick(data.whatsapp_url, SITE_SETTINGS_FALLBACK.whatsappUrl),
      address: {
        streetAddress: pick(data.street_address, SITE_SETTINGS_FALLBACK.address.streetAddress),
        addressLocality: pick(data.address_locality, SITE_SETTINGS_FALLBACK.address.addressLocality),
        addressRegion: pick(data.address_region, SITE_SETTINGS_FALLBACK.address.addressRegion),
        postalCode: data.postal_code?.trim() || SITE_SETTINGS_FALLBACK.address.postalCode,
        addressCountry: pick(data.address_country, SITE_SETTINGS_FALLBACK.address.addressCountry),
      },
      openingHours: data.opening_hours?.trim()
        ? [data.opening_hours.trim()]
        : SITE_SETTINGS_FALLBACK.openingHours,
      socialLinks: socialLinks.length > 0 ? socialLinks : SITE_SETTINGS_FALLBACK.socialLinks,
      parentOrganization: {
        name: pick(data.parent_organization_name, SITE_SETTINGS_FALLBACK.parentOrganization.name),
        url: parentUrl || SITE_SETTINGS_FALLBACK.parentOrganization.url,
      },
    };
  } catch (error) {
    logger.log('site_settings doc not available, using fallback:', error);
    return SITE_SETTINGS_FALLBACK;
  }
});
